import React, { useState, useEffect } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Typography,
  Chip,
  CircularProgress,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Tabs,
  Tab,
  Box,
  Card,
  CardContent,
  Avatar,
  Container,
  IconButton,
  Tooltip,
  Badge,
  TextField,
  InputAdornment,
} from "@mui/material";
import { getAllTickets, markTicketAsSeen } from "../../../Services/TicketsApi";
import ClintsLoginHistory from "./ClintsLoginHistory";
import SupportAgentIcon from "@mui/icons-material/SupportAgent";
import ConfirmationNumberIcon from "@mui/icons-material/ConfirmationNumber";
import LoginIcon from "@mui/icons-material/Login";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import AttachFileIcon from "@mui/icons-material/AttachFile";
import PersonIcon from "@mui/icons-material/Person";
import CategoryIcon from "@mui/icons-material/Category";
import MessageIcon from "@mui/icons-material/Message";
import EventIcon from "@mui/icons-material/Event";
import VisibilityIcon from "@mui/icons-material/Visibility";
import SearchIcon from "@mui/icons-material/Search";
import ClearIcon from "@mui/icons-material/Clear";
import DateRangeIcon from "@mui/icons-material/DateRange";
import FilterListIcon from "@mui/icons-material/FilterList";
import FormControl from "@mui/material/FormControl";
import Select from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";
import InputLabel from "@mui/material/InputLabel";

const statusColor = (seen) => {
  return seen === 0 ? "error" : "success";
};

const TechnicalSupportPageMain = () => {
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [confirmDialog, setConfirmDialog] = useState({
    open: false,
    ticketId: null,
    ticketCode: "",
    tenantName: "",
  });
  const [tabIndex, setTabIndex] = useState(0);
  const [searchQuery, setSearchQuery] = useState("");
  const [dateFilter, setDateFilter] = useState({
    startDate: "",
    endDate: "",
  });
  const [typeFilter, setTypeFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState("");

  // دالة لفتح Dialog التأكيد
  const handleOpenConfirmDialog = (ticket) => {
    setConfirmDialog({
      open: true,
      ticketId: ticket.id,
      ticketCode: ticket.code,
      tenantName: ticket.tenant,
    });
  };

  // دالة لإغلاق Dialog التأكيد
  const handleCloseConfirmDialog = () => {
    setConfirmDialog({
      open: false,
      ticketId: null,
      ticketCode: "",
      tenantName: "",
    });
  };

  // دالة لتأكيد مراجعة التذكرة
  const handleConfirmMarkAsSeen = async () => {
    try {
      await markTicketAsSeen(confirmDialog.ticketId);
      // تحديث حالة التذكرة في الـ state
      setTickets((prevTickets) =>
        prevTickets.map((ticket) =>
          ticket.id === confirmDialog.ticketId ? { ...ticket, seen: 1 } : ticket
        )
      );
      handleCloseConfirmDialog();
    } catch (error) {
      console.error("Error marking ticket as seen:", error);
    }
  };

  const handleTabChange = (event, newValue) => {
    setTabIndex(newValue);
  };

  useEffect(() => {
    const fetchTickets = async () => {
      try {
        const response = await getAllTickets();
        console.log("API Response:", response); // للتحقق من شكل البيانات

        // التحقق من أن البيانات موجودة وهي array
        if (
          response &&
          response.data &&
          response.data.data &&
          Array.isArray(response.data.data)
        ) {
          setTickets(response.data.data);
        } else {
          console.warn("Invalid data format:", response);
          setTickets([]);
        }
      } catch (error) {
        console.error("Error fetching tickets:", error);
        setTickets([]);
      } finally {
        setLoading(false);
      }
    };

    fetchTickets();
  }, []);

  if (loading) {
    return (
      <Container maxWidth="xl" sx={{ py: 8, textAlign: "center" }}>
        <CircularProgress size={60} />
        <Typography variant="h6" sx={{ mt: 2 }}>
          جاري تحميل البيانات...
        </Typography>
      </Container>
    );
  }

  // دالة البحث والفلترة في التذاكر
  const filteredTickets = tickets.filter((ticket) => {
    const query = searchQuery.toLowerCase();
    const matchesSearch =
      ticket.code?.toLowerCase().includes(query) ||
      ticket.ticket_type?.toLowerCase().includes(query) ||
      ticket.message?.toLowerCase().includes(query) ||
      ticket.tenant?.toLowerCase().includes(query) ||
      ticket.file?.toLowerCase().includes(query);

    // فلترة التاريخ
    let matchesDate = true;
    if (dateFilter.startDate || dateFilter.endDate) {
      const ticketDate = new Date(ticket.created_at);
      ticketDate.setHours(0, 0, 0, 0);

      if (dateFilter.startDate) {
        const startDate = new Date(dateFilter.startDate);
        startDate.setHours(0, 0, 0, 0);
        matchesDate = matchesDate && ticketDate >= startDate;
      }

      if (dateFilter.endDate) {
        const endDate = new Date(dateFilter.endDate);
        endDate.setHours(23, 59, 59, 999);
        matchesDate = matchesDate && ticketDate <= endDate;
      }
    }

    // فلترة النوع
    const matchesType = typeFilter === "" || ticket.ticket_type === typeFilter;

    // فلترة الحالة
    const matchesStatus =
      statusFilter === "" ||
      (statusFilter === "0" && ticket.seen === 0) ||
      (statusFilter === "1" && ticket.seen === 1);

    return matchesSearch && matchesDate && matchesType && matchesStatus;
  });

  const handleClearSearch = () => {
    setSearchQuery("");
  };

  const handleClearDateFilter = () => {
    setDateFilter({
      startDate: "",
      endDate: "",
    });
  };

  const handleClearAllFilters = () => {
    setSearchQuery("");
    setDateFilter({
      startDate: "",
      endDate: "",
    });
    setTypeFilter("");
    setStatusFilter("");
  };

  const isFiltered =
    searchQuery ||
    dateFilter.startDate ||
    dateFilter.endDate ||
    typeFilter ||
    statusFilter;

  // استخراج جميع أنواع التذاكر الفريدة
  const uniqueTicketTypes = [
    ...new Set(tickets.map((ticket) => ticket.ticket_type)),
  ].filter(Boolean);

  const unseenCount = tickets.filter((t) => t.seen === 0).length;

  return (
    <Container maxWidth="xl" sx={{ py: 4 }}>
      <Card
        elevation={3}
        sx={{
          mb: 3,
          background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
        }}
      >
        <CardContent>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <Avatar
                sx={{
                  bgcolor: "white",
                  color: "#667eea",
                  width: 50,
                  height: 50,
                }}
              >
                <SupportAgentIcon sx={{ fontSize: 32 }} />
              </Avatar>
              <Box>
                <Typography
                  variant="h4"
                  sx={{ color: "white", fontWeight: "bold" }}
                >
                  الدعم الفني
                </Typography>
                <Typography
                  variant="body1"
                  sx={{ color: "rgba(255,255,255,0.9)" }}
                >
                  إدارة تذاكر الدعم ومتابعة المستخدمين
                </Typography>
              </Box>
            </Box>
            {unseenCount > 0 && (
              <Badge
                badgeContent={unseenCount}
                color="error"
                sx={{
                  "& .MuiBadge-badge": {
                    fontSize: "1rem",
                    height: 28,
                    minWidth: 28,
                  },
                }}
              >
                <Chip
                  icon={<ErrorOutlineIcon />}
                  label={`${unseenCount} تذكرة جديدة`}
                  sx={{
                    bgcolor: "white",
                    color: "#f44336",
                    fontWeight: "bold",
                    px: 2,
                    py: 3,
                  }}
                />
              </Badge>
            )}
          </Box>
        </CardContent>
      </Card>

      <Card elevation={3} sx={{ borderRadius: 2 }}>
        <Tabs
          value={tabIndex}
          onChange={handleTabChange}
          variant="fullWidth"
          sx={{
            borderBottom: 1,
            borderColor: "divider",
            bgcolor: "#f5f5f5",
            "& .MuiTab-root": {
              fontSize: "1rem",
              fontWeight: "bold",
              minHeight: 72,
              transition: "all 0.3s",
              "&:hover": {
                bgcolor: "#e3f2fd",
              },
              "&.Mui-selected": {
                bgcolor: "white",
                color: "#1976d2",
              },
            },
          }}
        >
          <Tab
            label={
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <ConfirmationNumberIcon />
                تذاكر الدعم الفني
                {unseenCount > 0 && (
                  <Chip
                    label={unseenCount}
                    size="small"
                    color="error"
                    sx={{ ml: 1 }}
                  />
                )}
              </Box>
            }
          />
          <Tab
            label={
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <LoginIcon />
                سجل دخول العملاء{" "}
              </Box>
            }
          />
        </Tabs>

        <Box sx={{ p: 3 }}>
          {tabIndex === 0 && (
            <Box
              sx={{
                animation: "fadeIn 0.5s",
                "@keyframes fadeIn": {
                  from: { opacity: 0, transform: "translateY(10px)" },
                  to: { opacity: 1, transform: "translateY(0)" },
                },
              }}
            >
              {/* شريط البحث والفلاتر */}
              {Array.isArray(tickets) && tickets.length > 0 && (
                <>
                  {/* شريط البحث */}
                  <Card
                    variant="outlined"
                    sx={{
                      mb: 2,
                      bgcolor: "#f8f9fa",
                      border: "2px solid #e3f2fd",
                    }}
                  >
                    <CardContent>
                      <Box
                        sx={{ display: "flex", alignItems: "center", gap: 2 }}
                      >
                        <Avatar sx={{ bgcolor: "#1976d2" }}>
                          <SearchIcon />
                        </Avatar>
                        <TextField
                          fullWidth
                          placeholder="ابحث في التذاكر (رمز التذكرة، النوع، الرسالة، المستأجر، المرفق...)"
                          value={searchQuery}
                          onChange={(e) => setSearchQuery(e.target.value)}
                          variant="outlined"
                          sx={{
                            bgcolor: "white",
                            "& .MuiOutlinedInput-root": {
                              "&:hover fieldset": {
                                borderColor: "#1976d2",
                              },
                            },
                          }}
                          InputProps={{
                            startAdornment: (
                              <InputAdornment position="start">
                                <SearchIcon color="primary" />
                              </InputAdornment>
                            ),
                            endAdornment: searchQuery && (
                              <InputAdornment position="end">
                                <Tooltip title="مسح البحث">
                                  <IconButton
                                    onClick={handleClearSearch}
                                    edge="end"
                                    size="small"
                                    sx={{
                                      bgcolor: "#ffebee",
                                      "&:hover": {
                                        bgcolor: "#ffcdd2",
                                      },
                                    }}
                                  >
                                    <ClearIcon fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                              </InputAdornment>
                            ),
                          }}
                        />
                      </Box>
                    </CardContent>
                  </Card>

                  {/* فلتر التاريخ */}
                  <Card
                    variant="outlined"
                    sx={{
                      mb: 3,
                      bgcolor: "#fff3e0",
                      border: "2px solid #ffe0b2",
                    }}
                  >
                    <CardContent>
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: 2,
                          flexWrap: "wrap",
                        }}
                      >
                        <Avatar sx={{ bgcolor: "#ff9800" }}>
                          <FilterListIcon />
                        </Avatar>
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 2,
                            flex: 1,
                            flexWrap: "wrap",
                          }}
                        >
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              gap: 1,
                              minWidth: 200,
                            }}
                          >
                            <DateRangeIcon sx={{ color: "#ff9800" }} />
                            <Typography
                              variant="body2"
                              sx={{ fontWeight: "bold", color: "#ff9800" }}
                            >
                              من:
                            </Typography>
                            <TextField
                              type="date"
                              size="small"
                              value={dateFilter.startDate}
                              onChange={(e) =>
                                setDateFilter((prev) => ({
                                  ...prev,
                                  startDate: e.target.value,
                                }))
                              }
                              sx={{
                                bgcolor: "white",
                                minWidth: 150,
                              }}
                              InputLabelProps={{
                                shrink: true,
                              }}
                            />
                          </Box>
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              gap: 1,
                              minWidth: 200,
                            }}
                          >
                            <DateRangeIcon sx={{ color: "#ff9800" }} />
                            <Typography
                              variant="body2"
                              sx={{ fontWeight: "bold", color: "#ff9800" }}
                            >
                              إلى:
                            </Typography>
                            <TextField
                              type="date"
                              size="small"
                              value={dateFilter.endDate}
                              onChange={(e) =>
                                setDateFilter((prev) => ({
                                  ...prev,
                                  endDate: e.target.value,
                                }))
                              }
                              sx={{
                                bgcolor: "white",
                                minWidth: 150,
                              }}
                              InputLabelProps={{
                                shrink: true,
                              }}
                            />
                          </Box>
                          {(dateFilter.startDate || dateFilter.endDate) && (
                            <Tooltip title="مسح فلتر التاريخ">
                              <IconButton
                                onClick={handleClearDateFilter}
                                size="small"
                                sx={{
                                  bgcolor: "#ffebee",
                                  "&:hover": {
                                    bgcolor: "#ffcdd2",
                                  },
                                }}
                              >
                                <ClearIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          )}
                        </Box>
                        {isFiltered && (
                          <Box
                            sx={{
                              display: "flex",
                              gap: 1,
                              alignItems: "center",
                            }}
                          >
                            <Chip
                              label={`${filteredTickets.length} نتيجة`}
                              color="warning"
                              sx={{ fontWeight: "bold" }}
                            />
                            <Tooltip title="مسح جميع الفلاتر">
                              <Button
                                variant="outlined"
                                color="error"
                                size="small"
                                startIcon={<ClearIcon />}
                                onClick={handleClearAllFilters}
                                sx={{ fontWeight: "bold" }}
                              >
                                مسح الكل
                              </Button>
                            </Tooltip>
                          </Box>
                        )}
                      </Box>
                    </CardContent>
                  </Card>

                  {/* فلاتر النوع والحالة */}
                  <Card
                    variant="outlined"
                    sx={{
                      mb: 3,
                      bgcolor: "#f3e5f5",
                      border: "2px solid #e1bee7",
                    }}
                  >
                    <CardContent>
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: 2,
                          flexWrap: "wrap",
                        }}
                      >
                        <Avatar sx={{ bgcolor: "#9c27b0" }}>
                          <CategoryIcon />
                        </Avatar>

                        {/* فلتر النوع */}
                        <Box sx={{ minWidth: 250 }}>
                          <FormControl
                            fullWidth
                            size="small"
                            sx={{ bgcolor: "white" }}
                          >
                            <InputLabel>فلترة حسب النوع</InputLabel>
                            <Select
                              value={typeFilter}
                              label="فلترة حسب النوع"
                              onChange={(e) => setTypeFilter(e.target.value)}
                            >
                              <MenuItem value="">
                                <em>جميع الأنواع</em>
                              </MenuItem>
                              {uniqueTicketTypes.map((type) => (
                                <MenuItem key={type} value={type}>
                                  <Box
                                    sx={{
                                      display: "flex",
                                      alignItems: "center",
                                      gap: 1,
                                    }}
                                  >
                                    <CategoryIcon
                                      fontSize="small"
                                      color="primary"
                                    />
                                    {type}
                                  </Box>
                                </MenuItem>
                              ))}
                            </Select>
                          </FormControl>
                        </Box>

                        {/* فلتر الحالة */}
                        <Box sx={{ minWidth: 250 }}>
                          <FormControl
                            fullWidth
                            size="small"
                            sx={{ bgcolor: "white" }}
                          >
                            <InputLabel>فلترة حسب الحالة</InputLabel>
                            <Select
                              value={statusFilter}
                              label="فلترة حسب الحالة"
                              onChange={(e) => setStatusFilter(e.target.value)}
                            >
                              <MenuItem value="">
                                <em>جميع الحالات</em>
                              </MenuItem>
                              <MenuItem value="0">
                                <Box
                                  sx={{
                                    display: "flex",
                                    alignItems: "center",
                                    gap: 1,
                                  }}
                                >
                                  <ErrorOutlineIcon
                                    fontSize="small"
                                    color="error"
                                  />
                                  <span>تذاكر جديدة</span>
                                  <Chip
                                    label={
                                      tickets.filter((t) => t.seen === 0).length
                                    }
                                    size="small"
                                    color="error"
                                  />
                                </Box>
                              </MenuItem>
                              <MenuItem value="1">
                                <Box
                                  sx={{
                                    display: "flex",
                                    alignItems: "center",
                                    gap: 1,
                                  }}
                                >
                                  <CheckCircleIcon
                                    fontSize="small"
                                    color="success"
                                  />
                                  <span>تمت المراجعة</span>
                                  <Chip
                                    label={
                                      tickets.filter((t) => t.seen === 1).length
                                    }
                                    size="small"
                                    color="success"
                                  />
                                </Box>
                              </MenuItem>
                            </Select>
                          </FormControl>
                        </Box>

                        {/* زر مسح الفلاتر */}
                        {(typeFilter || statusFilter) && (
                          <Tooltip title="مسح فلاتر النوع والحالة">
                            <IconButton
                              onClick={() => {
                                setTypeFilter("");
                                setStatusFilter("");
                              }}
                              size="small"
                              sx={{
                                bgcolor: "#ffebee",
                                "&:hover": {
                                  bgcolor: "#ffcdd2",
                                },
                              }}
                            >
                              <ClearIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        )}

                        {/* عرض النتائج */}
                        {isFiltered && (
                          <Box
                            sx={{
                              display: "flex",
                              gap: 1,
                              alignItems: "center",
                              ml: "auto",
                            }}
                          >
                            <Chip
                              label={`${filteredTickets.length} نتيجة`}
                              color="secondary"
                              sx={{ fontWeight: "bold" }}
                            />
                            <Tooltip title="مسح جميع الفلاتر">
                              <Button
                                variant="outlined"
                                color="error"
                                size="small"
                                startIcon={<ClearIcon />}
                                onClick={handleClearAllFilters}
                                sx={{ fontWeight: "bold" }}
                              >
                                مسح الكل
                              </Button>
                            </Tooltip>
                          </Box>
                        )}
                      </Box>

                      {/* عرض الفلاتر النشطة */}
                      {(typeFilter || statusFilter) && (
                        <Box
                          sx={{
                            display: "flex",
                            gap: 1,
                            mt: 2,
                            flexWrap: "wrap",
                          }}
                        >
                          {typeFilter && (
                            <Chip
                              label={`النوع: ${typeFilter}`}
                              color="secondary"
                              onDelete={() => setTypeFilter("")}
                              sx={{ fontWeight: "bold" }}
                            />
                          )}
                          {statusFilter && (
                            <Chip
                              label={
                                statusFilter === "0"
                                  ? "الحالة: جديدة"
                                  : "الحالة: تمت المراجعة"
                              }
                              color="secondary"
                              onDelete={() => setStatusFilter("")}
                              sx={{ fontWeight: "bold" }}
                            />
                          )}
                        </Box>
                      )}
                    </CardContent>
                  </Card>
                </>
              )}

              {Array.isArray(tickets) && tickets.length > 0 ? (
                filteredTickets.length > 0 ? (
                  <TableContainer
                    component={Paper}
                    elevation={0}
                    sx={{ borderRadius: 2, border: "1px solid #e0e0e0" }}
                  >
                    <Table>
                      <TableHead>
                        <TableRow sx={{ bgcolor: "#f5f5f5" }}>
                          <TableCell
                            sx={{ fontWeight: "bold", fontSize: "1rem" }}
                          >
                            رمز التذكرة
                          </TableCell>
                          <TableCell
                            sx={{ fontWeight: "bold", fontSize: "1rem" }}
                          >
                            النوع
                          </TableCell>
                          <TableCell
                            sx={{ fontWeight: "bold", fontSize: "1rem" }}
                          >
                            الرسالة
                          </TableCell>
                          <TableCell
                            sx={{ fontWeight: "bold", fontSize: "1rem" }}
                          >
                            المرفق
                          </TableCell>
                          <TableCell
                            sx={{ fontWeight: "bold", fontSize: "1rem" }}
                          >
                            المستأجر
                          </TableCell>
                          <TableCell
                            sx={{ fontWeight: "bold", fontSize: "1rem" }}
                          >
                            الحالة
                          </TableCell>
                          <TableCell
                            sx={{ fontWeight: "bold", fontSize: "1rem" }}
                          >
                            التاريخ
                          </TableCell>
                          <TableCell
                            sx={{ fontWeight: "bold", fontSize: "1rem" }}
                          >
                            الإجراء
                          </TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {filteredTickets.map((ticket) => (
                          <TableRow
                            key={ticket.id}
                            sx={{
                              "&:hover": {
                                bgcolor:
                                  ticket.seen === 0 ? "#fff3e0" : "#f9f9f9",
                                transition: "all 0.2s",
                              },
                              bgcolor:
                                ticket.seen === 0 ? "#fffde7" : "transparent",
                            }}
                          >
                            <TableCell>
                              <Box
                                sx={{
                                  display: "flex",
                                  alignItems: "center",
                                  gap: 1,
                                }}
                              >
                                <Avatar
                                  sx={{
                                    bgcolor:
                                      ticket.seen === 0 ? "#f44336" : "#4caf50",
                                    width: 32,
                                    height: 32,
                                  }}
                                >
                                  <ConfirmationNumberIcon fontSize="small" />
                                </Avatar>
                                <Typography
                                  sx={{
                                    fontWeight: "bold",
                                    fontFamily: "monospace",
                                  }}
                                >
                                  {ticket.code}
                                </Typography>
                              </Box>
                            </TableCell>
                            <TableCell>
                              <Chip
                                icon={<CategoryIcon />}
                                label={ticket.ticket_type}
                                size="small"
                                color="primary"
                                variant="outlined"
                              />
                            </TableCell>
                            <TableCell sx={{ maxWidth: 300 }}>
                              <Box
                                sx={{
                                  display: "flex",
                                  alignItems: "flex-start",
                                  gap: 1,
                                }}
                              >
                                <MessageIcon
                                  sx={{ color: "#666", fontSize: 18, mt: 0.3 }}
                                />
                                <Typography
                                  variant="body2"
                                  color="text.secondary"
                                >
                                  {ticket.message}
                                </Typography>
                              </Box>
                            </TableCell>
                            <TableCell>
                              {ticket.file ? (
                                <Tooltip title={ticket.file}>
                                  <Chip
                                    icon={<AttachFileIcon />}
                                    label="مرفق"
                                    size="small"
                                    color="info"
                                    clickable
                                  />
                                </Tooltip>
                              ) : (
                                <Typography
                                  variant="body2"
                                  color="text.secondary"
                                >
                                  لا يوجد
                                </Typography>
                              )}
                            </TableCell>
                            <TableCell>
                              <Box
                                sx={{
                                  display: "flex",
                                  alignItems: "center",
                                  gap: 1,
                                }}
                              >
                                <Avatar
                                  sx={{
                                    bgcolor: "#2196f3",
                                    width: 28,
                                    height: 28,
                                  }}
                                >
                                  <PersonIcon fontSize="small" />
                                </Avatar>
                                <Typography sx={{ fontWeight: "bold" }}>
                                  {ticket.tenant}
                                </Typography>
                              </Box>
                            </TableCell>
                            <TableCell>
                              <Chip
                                icon={
                                  ticket.seen === 0 ? (
                                    <ErrorOutlineIcon />
                                  ) : (
                                    <CheckCircleIcon />
                                  )
                                }
                                label={
                                  ticket.seen === 0 ? "جديدة" : "تمت المراجعة"
                                }
                                color={statusColor(ticket.seen)}
                                size="small"
                                sx={{ fontWeight: "bold" }}
                              />
                            </TableCell>
                            <TableCell>
                              <Box
                                sx={{
                                  display: "flex",
                                  alignItems: "center",
                                  gap: 0.5,
                                }}
                              >
                                <EventIcon
                                  sx={{ color: "#666", fontSize: 18 }}
                                />
                                <Typography variant="body2">
                                  {new Date(
                                    ticket.created_at
                                  ).toLocaleDateString("ar")}
                                </Typography>
                              </Box>
                            </TableCell>
                            <TableCell>
                              {ticket.seen === 0 ? (
                                <Tooltip title="تم مراجعة التذكرة">
                                  <Button
                                    variant="contained"
                                    color="success"
                                    size="small"
                                    startIcon={<CheckCircleIcon />}
                                    onClick={() =>
                                      handleOpenConfirmDialog(ticket)
                                    }
                                    sx={{
                                      fontWeight: "bold",
                                      "&:hover": {
                                        transform: "scale(1.05)",
                                        transition: "all 0.2s",
                                      },
                                    }}
                                  >
                                    مراجعة
                                  </Button>
                                </Tooltip>
                              ) : (
                                <Chip
                                  icon={<VisibilityIcon />}
                                  label="تمت المراجعة"
                                  size="small"
                                  color="default"
                                />
                              )}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                ) : (
                  <Box
                    sx={{
                      textAlign: "center",
                      py: 8,
                      bgcolor: "#fff3e0",
                      borderRadius: 2,
                      border: "2px dashed #ff9800",
                    }}
                  >
                    <SearchIcon
                      sx={{ fontSize: 80, color: "#ff9800", mb: 2 }}
                    />
                    <Typography
                      variant="h6"
                      color="text.secondary"
                      sx={{ mb: 1 }}
                    >
                      لا توجد نتائج للبحث
                    </Typography>
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{ mb: 2 }}
                    >
                      جرب تغيير معايير البحث أو الفلترة
                    </Typography>
                    <Box
                      sx={{
                        display: "flex",
                        gap: 1,
                        justifyContent: "center",
                        flexWrap: "wrap",
                      }}
                    >
                      {searchQuery && (
                        <Chip
                          label={`البحث: "${searchQuery}"`}
                          color="warning"
                          onDelete={handleClearSearch}
                          sx={{ fontWeight: "bold" }}
                        />
                      )}
                      {dateFilter.startDate && (
                        <Chip
                          label={`من: ${new Date(
                            dateFilter.startDate
                          ).toLocaleDateString("ar")}`}
                          color="warning"
                          sx={{ fontWeight: "bold" }}
                        />
                      )}
                      {dateFilter.endDate && (
                        <Chip
                          label={`إلى: ${new Date(
                            dateFilter.endDate
                          ).toLocaleDateString("ar")}`}
                          color="warning"
                          sx={{ fontWeight: "bold" }}
                        />
                      )}
                      {typeFilter && (
                        <Chip
                          label={`النوع: ${typeFilter}`}
                          color="warning"
                          onDelete={() => setTypeFilter("")}
                          sx={{ fontWeight: "bold" }}
                        />
                      )}
                      {statusFilter && (
                        <Chip
                          label={
                            statusFilter === "0"
                              ? "الحالة: جديدة"
                              : "الحالة: تمت المراجعة"
                          }
                          color="warning"
                          onDelete={() => setStatusFilter("")}
                          sx={{ fontWeight: "bold" }}
                        />
                      )}
                    </Box>
                    <Button
                      variant="contained"
                      color="warning"
                      startIcon={<ClearIcon />}
                      onClick={handleClearAllFilters}
                      sx={{ mt: 2, fontWeight: "bold" }}
                    >
                      مسح جميع الفلاتر
                    </Button>
                  </Box>
                )
              ) : (
                <Box
                  sx={{
                    textAlign: "center",
                    py: 8,
                    bgcolor: "#f5f5f5",
                    borderRadius: 2,
                  }}
                >
                  <SupportAgentIcon
                    sx={{ fontSize: 80, color: "#bdbdbd", mb: 2 }}
                  />
                  <Typography variant="h6" color="text.secondary">
                    لا توجد تذاكر دعم فني
                  </Typography>
                </Box>
              )}
            </Box>
          )}
          {tabIndex === 1 && (
            <Box
              sx={{
                animation: "fadeIn 0.5s",
                "@keyframes fadeIn": {
                  from: { opacity: 0, transform: "translateY(10px)" },
                  to: { opacity: 1, transform: "translateY(0)" },
                },
              }}
            >
              <ClintsLoginHistory />
            </Box>
          )}
        </Box>
      </Card>

      {/* Dialog التأكيد */}
      <Dialog
        open={confirmDialog.open}
        onClose={handleCloseConfirmDialog}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: { borderRadius: 2 },
        }}
      >
        <DialogTitle
          sx={{
            bgcolor: "#4caf50",
            color: "white",
            display: "flex",
            alignItems: "center",
            gap: 1,
          }}
        >
          <CheckCircleIcon />
          تأكيد مراجعة الشكوى
        </DialogTitle>
        <DialogContent sx={{ mt: 3 }}>
          <Box sx={{ textAlign: "center", py: 2 }}>
            <CheckCircleIcon sx={{ fontSize: 60, color: "#4caf50", mb: 2 }} />
            <Typography variant="h6" sx={{ mb: 2 }}>
              هل أنت متأكد من مراجعة هذه الشكوى؟
            </Typography>
            <Card variant="outlined" sx={{ mt: 2, bgcolor: "#f5f5f5" }}>
              <CardContent>
                <Box
                  sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}
                >
                  <ConfirmationNumberIcon color="primary" />
                  <Typography variant="body1">
                    <strong>رقم الشكوى:</strong>
                  </Typography>
                  <Chip
                    label={confirmDialog.ticketCode}
                    color="primary"
                    sx={{ fontWeight: "bold", fontFamily: "monospace" }}
                  />
                </Box>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <PersonIcon color="secondary" />
                  <Typography variant="body1">
                    <strong>اسم المستأجر:</strong>
                  </Typography>
                  <Chip
                    label={confirmDialog.tenantName}
                    color="secondary"
                    sx={{ fontWeight: "bold" }}
                  />
                </Box>
              </CardContent>
            </Card>
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 2.5, bgcolor: "#f5f5f5" }}>
          <Button
            onClick={handleCloseConfirmDialog}
            variant="outlined"
            size="large"
            sx={{ minWidth: 100 }}
          >
            إلغاء
          </Button>
          <Button
            onClick={handleConfirmMarkAsSeen}
            variant="contained"
            color="success"
            size="large"
            startIcon={<CheckCircleIcon />}
            sx={{
              minWidth: 120,
              fontWeight: "bold",
            }}
          >
            تأكيد المراجعة
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default TechnicalSupportPageMain;
